import mapxProjectApi from "api/projectApi";
import mapxProjectInsightsApi from "api/projectApi/projectStatsApi";
import axios, {AxiosResponse} from "axios";
import {successResponse} from "helpers/map";

import {
	bulkProjectCandidateStatusChange,
	getProjectCandidateIDsDone,
	getProjectCandidateStatusesFailed,
	getProjectCandidateStatusesInit,
	getProjectProgressStatusesFailed,
	getProjectProgressStatusesInit,
	getProjectStatsFailed,
	getProjectStatsInit,
	setCandidateIdsForAssessment,
	setProjectCandidateStatuses,
	setProjectCandidateStatusesFinished,
	setProjectProgressStatuses,
	setProjectProgressStatusesFinished,
	setProjectStats,
} from "store/project/projectActions";
import {TAppDispatch, TRootState} from "types";
import {STProject, STProjectProgressStatus} from "api/projectApi/types";
import projectCandidateApi from "api/projectApi/projectCandidatesApi";
import {selectedCandidateIdsForAssessmentSelector} from "store/project/Assessment/candidateScoringSelectors";
import {projectSelector} from "store/project-list/projectListSelectors";
import {TProjectCandidatesStatusFormBody} from "api/projectApi/projectCandidatesApi/types";
import {TBulkStatusChangeParams} from "mapx-pages/Project/SavedPeople/SavedPeopleHeaderSection/types";
import {
	getTargetListCandidateIDsDone,
	updateAPCandidateByKey,
} from "store/target-list/targetListActions";
import {clearCancelToken, getCancelToken} from "api/cancelTokens";
import {
	GET_PROJECT_STATISTICS_TOKEN,
	PROJECT_INDUSTRIES_REPORT_TOKEN,
} from "api/requestCancelTokenStrings";
import {TProjectCandidateStatusDropdownOption} from "mapx-components/Cards/ProjectCandidateCard/types";
import {Mixpanel} from "helpers/mixpanel";
import {userEmailSelector} from "store/user/userSelectors";
import {SICandidate, TPaginationParam} from "api/candidateApi/types";

import {getPaginatedProjectSavedPeople} from "store/project/projectCandidatesAsyncActions";
import {getTargetListCandidates} from "store/target-list/targetListCandidatesAsyncActions";
import {
	targetListCandidateIDsSelector,
	targetListIDSelector,
} from "store/target-list/targetListSelectors";
import {industryReportPaginationSelector} from "store/project/projectSelectors";
import {globalPerPageSelector} from "store/userPreference/userPreferenceSelectors";
import {
	getIndustriesReportFailed,
	getIndustriesReportFetch,
	getIndustriesReportSuccess,
} from "store/project/ProjectInsights/projectInsightsActions";

export const getIndustryReportData =
	(projectID: number, pagination?: TPaginationParam) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const state = getState();

		dispatch(getIndustriesReportFetch());

		const cancelTokenSource = getCancelToken(PROJECT_INDUSTRIES_REPORT_TOKEN);

		try {
			const config = {
				cancelToken: cancelTokenSource.token,
			};

			const currentPagination = industryReportPaginationSelector(state);

			const globalPerPageValue = globalPerPageSelector(state);

			const finalPagination = {
				page: pagination?.page ?? currentPagination?.page ?? 1,
				per_page: globalPerPageValue ?? pagination?.per_page ?? 20,
			};

			const response: AxiosResponse =
				await mapxProjectInsightsApi.getPaginatedIndustriesReport(
					projectID,
					finalPagination,
					config,
				);

			if (successResponse(response, 200)) {
				const data = response.data;

				dispatch(getIndustriesReportSuccess(data));
			}
		} catch (e) {
			if (axios.isCancel(e)) {
				console.log("Request canceled:", e.message);
			} else {
				console.error(e);
				dispatch(getIndustriesReportFailed());
				console.log("failed");
			}
		} finally {
			clearCancelToken(PROJECT_INDUSTRIES_REPORT_TOKEN); // Ensure token is cleared
		}
	};

export const getProjectStats = (id: number) => async (dispatch: TAppDispatch) => {
	dispatch(getProjectStatsInit());

	const config = {
		cancelToken: getCancelToken(GET_PROJECT_STATISTICS_TOKEN).token,
	};

	try {
		const response = await mapxProjectInsightsApi.getProjectStats(id, config);

		if (successResponse(response, 200)) {
			const data = response.data;

			dispatch(setProjectStats(data));
		}

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (axios.isCancel()) {
			dispatch(getIndustriesReportFailed());
		}
	} catch (e) {
		if (axios.isCancel(e)) {
			console.log("Request canceled:", e.message);
		} else {
			console.error(e);
			dispatch(getProjectStatsFailed());
		}
	} finally {
		clearCancelToken(GET_PROJECT_STATISTICS_TOKEN);
	}
};

export const getProjectProgressStatuses = () => async (dispatch: TAppDispatch) => {
	try {
		dispatch(getProjectProgressStatusesInit());

		const response: AxiosResponse = await mapxProjectApi.getProjectProgressStatuses();

		if (successResponse(response, 200)) {
			const data = response.data.results as STProjectProgressStatus[];

			if (data?.length > 0) {
				dispatch(setProjectProgressStatuses(data));
			} else {
				dispatch(setProjectProgressStatusesFinished());
			}
		}
	} catch (error) {
		dispatch(getProjectProgressStatusesFailed());

		return {error};
	}
};

export const changeProjectCandidateStatus =
	({isRecommended, statusId}: TBulkStatusChangeParams) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		try {
			const state = getState();

			const candidateIds: number[] = selectedCandidateIdsForAssessmentSelector(state);

			const project = projectSelector(state) as STProject;

			const formBody: TProjectCandidatesStatusFormBody = {
				candidate_ids: candidateIds,
			};

			if (isRecommended !== undefined) {
				formBody.is_recommended = isRecommended;
			}

			if (statusId !== undefined) {
				formBody.progress_status_id = statusId;
			}

			dispatch(bulkProjectCandidateStatusChange(true));

			const response = await projectCandidateApi.updateProjectCandidatesStatus(
				project.id,
				formBody,
			);

			dispatch(bulkProjectCandidateStatusChange(false));

			if (successResponse(response, 200)) {
				dispatch(getPaginatedProjectSavedPeople());

				const userEmail = userEmailSelector(state);

				if (isRecommended !== undefined) {
					Mixpanel.track(`Candidate Recommended`, {
						name: `${window.name}`,
						pageTitle: `${window.name}`,
						url: window.location.href,
						distinct_id: userEmail,
					});
				}

				if (statusId !== undefined) {
					Mixpanel.track(`Candidate Status Changed`, {
						name: `${window.name}`,
						pageTitle: `${window.name}`,
						url: window.location.href,
						distinct_id: userEmail,
					});
				}

				return response.data;
			}
		} catch (error) {
			dispatch(bulkProjectCandidateStatusChange(false));

			return {error};
		}
	};

export const removeSelectedCandidatesFromProject =
	() => async (dispatch: TAppDispatch, getState: () => TRootState) => {
		try {
			const state = getState();

			const candidateIds: number[] = selectedCandidateIdsForAssessmentSelector(state);

			const allCandidateIds: number[] = targetListCandidateIDsSelector(state);

			const project = projectSelector(state) as STProject;

			const formBody = {
				candidate_ids: candidateIds,
			};

			const response = await projectCandidateApi.deleteProjectCandidatesStatus(
				project.id,
				formBody,
			);

			if (successResponse(response, 200)) {
				dispatch(getTargetListCandidates());

				const updatedAllCandidateIds = allCandidateIds.filter(
					(id) => !candidateIds.includes(id),
				);

				dispatch(getTargetListCandidateIDsDone(updatedAllCandidateIds));

				dispatch(getProjectCandidateIDsDone(updatedAllCandidateIds));

				dispatch(getPaginatedProjectSavedPeople());

				dispatch(setCandidateIdsForAssessment([]));

				return response.data;
			}
		} catch (error) {
			return {error};
		}
	};

export const changeCandidateStatusByCandidateId =
	(candidate: SICandidate, status: TProjectCandidateStatusDropdownOption) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		try {
			const state = getState();

			dispatch(getProjectCandidateStatusesInit());

			const project = projectSelector(state) as STProject;

			const formBody = {
				candidate_ids: [candidate.id],
				progress_status_id: status.value,
			};

			const response = await projectCandidateApi.updateProjectCandidatesStatus(
				project.id,
				formBody,
			);

			if (successResponse(response, 200)) {
				const payload = {
					id: candidate.id,
					key: "progress_status",
					value: status.value ? status.name : null,
					previousStatus: candidate.progress_status,
					status: status.value ? status.name : candidate.progress_status,
				};

				dispatch(updateAPCandidateByKey(payload));

				return response.data;
			}
		} catch (error) {
			console.error("error in changeCandidateStatusByCandidateId function: ", error);

			dispatch(getProjectCandidateStatusesFailed());

			return {error};
		}
	};

export const getProjectCandidateStatuses =
	() => async (dispatch: TAppDispatch, getState: () => TRootState) => {
		try {
			dispatch(getProjectCandidateStatusesInit());

			const state = getState();

			const targetListID = targetListIDSelector(state);

			const response: AxiosResponse = await mapxProjectApi.getProjectCandidateStatuses(
				targetListID,
			);

			if (successResponse(response, 200)) {
				const data = response.data.results as STProjectProgressStatus[];

				if (data?.length > 0) {
					dispatch(setProjectCandidateStatuses(data));
				} else {
					dispatch(setProjectCandidateStatusesFinished());
				}
			}
		} catch (error) {
			dispatch(getProjectCandidateStatusesFailed());

			return {error};
		}
	};
